import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AccountsPage } from '../features/accounts/AccountsPage';
import { CategoriasPage } from '../features/categories/CategoriasPage';
import { AcceptInvitePage } from '../features/auth/AcceptInvitePage';
import { BootstrapPage } from '../features/auth/BootstrapPage';
import { LoginPage } from '../features/auth/LoginPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { HouseholdPage } from '../features/household/HouseholdPage';
import { ImportPage } from '../features/import/ImportPage';
import { ConnectionsPage } from '../features/openfinance/ConnectionsPage';
import { PayablesPage } from '../features/payables/PayablesPage';
import { PatrimonioPage } from '../features/patrimonio/PatrimonioPage';
import { RulesPage } from '../features/rules/RulesPage';
import { SecurityPage } from '../features/security/SecurityPage';
import { Icone, type NomeDoIcone } from '../components/Icone';
import { Sino } from '../features/notificacoes/Sino';
import { NotificacoesPage } from '../features/notificacoes/NotificacoesPage';
import { TransactionsPage } from '../features/transactions/TransactionsPage';
import { ApiError } from '../lib/api';
import { loadTheme, saveTheme, type ThemeName } from '../lib/theme';
import { AuthProvider, useAuth } from './auth-context';

/**
 * A tela se atualiza sozinha.
 *
 * Antes era preciso recarregar para ver um lancamento novo, o que numa tela
 * de dinheiro e pior do que parece: a pessoa olha um saldo desatualizado sem
 * nenhuma indicacao de que ele esta velho.
 *
 * Duas fontes de atualizacao, e nenhuma delas e "tempo real" de verdade —
 * porque o banco nao empurra nada:
 *
 *  - ao voltar para a aba, sempre. E o momento em que alguem pergunta "como
 *    esta?", e e de graca: zero requisicao enquanto a aba esta em segundo
 *    plano.
 *  - a cada minuto, enquanto a aba esta visivel. Um minuto e curto o bastante
 *    para o numero nunca parecer parado e longo o bastante para nao pesar na
 *    VPS, que divide dois nucleos com outros dois sistemas.
 *
 * O que encurta a espera de verdade e o aviso da Pluggy, que dispara a
 * varredura assim que o banco entrega o lancamento. Isto aqui so garante que,
 * quando o dado chegar, a tela mostre — sem ninguem apertar F5.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      retry: (failureCount, error) => {
        // Nao insistir em 401/403: o cliente ja tentou o refresh uma vez.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/primeiro-acesso" element={<BootstrapPage />} />
            <Route
              path="/entrar"
              element={
                <PublicOnly>
                  <LoginPage />
                </PublicOnly>
              }
            />
            <Route path="/convite/:token" element={<AcceptInvitePage />} />

            <Route element={<ProtectedShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="/contas" element={<AccountsPage />} />
              <Route path="/lancamentos" element={<TransactionsPage />} />
              <Route path="/patrimonio" element={<PatrimonioPage />} />
              <Route path="/categorias" element={<CategoriasPage />} />
              <Route path="/notificacoes" element={<NotificacoesPage />} />
              {/* A tela de recebedores virou o grafico do dashboard. O
                  endereco antigo continua respondendo, para nao quebrar um
                  favorito, e leva para onde a informacao foi. */}
              <Route path="/recebedores" element={<Navigate to="/" replace />} />
              <Route path="/a-pagar" element={<PayablesPage />} />
              <Route path="/importar" element={<ImportPage />} />
              <Route path="/conexoes" element={<ConnectionsPage />} />
              <Route path="/regras" element={<RulesPage />} />
              <Route path="/casa" element={<HouseholdPage />} />
              <Route path="/seguranca" element={<SecurityPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/**
 * Um item do menu: desenho, nome e, quando ha, o numero pendente.
 *
 * O nome fica num `<span>` proprio para poder crescer e empurrar o
 * distintivo para a direita sem mexer no alinhamento do icone.
 */
function Item({
  para,
  icone,
  fim = false,
  children,
}: {
  para: string;
  icone: NomeDoIcone;
  fim?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink to={para} end={fim}>
      <Icone nome={icone} />
      <span className="rotulo">{children}</span>
    </NavLink>
  );
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ProtectedShell() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [theme, setTheme] = useState<ThemeName>(loadTheme);

  useEffect(() => {
    saveTheme(theme);
  }, [theme]);

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">financas</div>

        <nav className="nav" aria-label="Navegacao principal">
          <Item para="/" icone="dashboard" fim>
            Dashboard
          </Item>
          <Item para="/lancamentos" icone="lancamentos">
            Lancamentos
          </Item>
          <Item para="/contas" icone="contas">
            Contas
          </Item>
          <Item para="/patrimonio" icone="patrimonio">
            Patrimonio
          </Item>
          <Item para="/categorias" icone="categorias">
            Categorias
          </Item>
          <Item para="/a-pagar" icone="a-pagar">
            Compromissos
          </Item>
          <Item para="/importar" icone="importar">
            Importar
          </Item>
          <Item para="/conexoes" icone="conexoes">
            Conexoes
          </Item>
          <Item para="/regras" icone="regras">
            Regras
          </Item>
          <Item para="/casa" icone="casa">
            Casa
          </Item>
          <Item para="/seguranca" icone="seguranca">
            Seguranca
          </Item>
        </nav>
      </aside>

      <div className="conteudo">
        {/*
          A barra de cima existe por causa do sino.

          Aviso nao e destino: ele precisa alcancar a pessoa em qualquer tela,
          e no menu lateral so alcancava quem estava olhando para o menu. O
          que veio junto — tema e sair — estava no pe da barra lateral, onde
          disputava espaco com doze itens de navegacao sem ser navegacao.
        */}
        <header className="topbar">
          <div className="topbar-acoes">
            <Sino />
            <button
              type="button"
              className="btn ghost"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
              title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
            >
              {theme === 'dark' ? 'Claro' : 'Escuro'}
            </button>
            <span className="topbar-usuario" title={user.email}>
              {user.name.split(' ')[0]}
            </span>
            <button type="button" className="btn ghost" onClick={() => void signOut()}>
              Sair
            </button>
          </div>
        </header>

        <Outlet />
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="loading" role="status">
      Carregando...
    </div>
  );
}
